import {
  JsonHubProtocol,
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
  HubConnection,
  IHttpConnectionOptions,
} from "@microsoft/signalr";
import {AnyAction, Dispatch, Middleware, MiddlewareAPI} from "redux";
import {USER_FOUND} from "redux-oidc";
import {UserFoundAction} from "../actions/OidcActionTypes";
import {receiveNotification, receiveReadNotification} from "../features/notifications/data/notificationSlice";
import {} from "../actions/NotificationActionTypes";
import AppSettings from "../types/AppSettings";
import {SignalREventHandler} from "../types/SignalREventHandler";
import {SignalRState} from "../types/SignalRState";
import {NotificationEvent} from "../types/NotificationEvent";
import {NotificationReadEvent} from "../types/NotificationReadEvent";
import { SIGNALR_CONNECTED, SIGNALR_DISCONNECTED, SignalRConnectedAction, SignalRDisconnectedAction } from "../actions/SignalRActionTypes";

// Connection
const signalRState: SignalRState = {
  accessTokenExpires: 0,
  connection: new HubConnectionBuilder().withUrl(`${window.location}/api/signalr/hub`).build(),
};

// Function to re-initiate connection to the hub (with a slight leading delay)
async function RestartSignalRConnection(dispatch: any): Promise<void> {
  console.debug(`[SignalR] Connection State: ${signalRState.connection.state}`);
  let dispatchAction: SignalRDisconnectedAction = {
    type: SIGNALR_DISCONNECTED,
  };
  dispatch(dispatchAction);
  await new Promise((resolve) => setTimeout(resolve, 1000));
  await StartSignalRConnection(dispatch);
}

// Function to initiate the connection to the hub (retries with exponential back-off)
async function StartSignalRConnection(dispatch: any): Promise<void> {
  console.debug("[SignalR] Starting connection...");
  let retries = 0;
  const initialDelay = 2;
  const maxDelay = 60;
  while (signalRState.connection.state !== HubConnectionState.Connected && retries < 30) {
    try {
      await signalRState.connection.start();
      console.debug(`[SignalR] Connection State: ${signalRState.connection.state}`);
      const dispatchAction: SignalRConnectedAction = {
        type: SIGNALR_CONNECTED,
      };
      dispatch(dispatchAction);
      return;
    } catch (err) {
      retries++;
      let delay = initialDelay ** retries < maxDelay ? initialDelay ** retries : maxDelay;
      console.error(`[SignalR] Connection Error. Retrying in ${delay} seconds...`);
      await new Promise((resolve) => setTimeout(resolve, delay * 1000));
    }
  }
}

// Function to subscribe to notifications
async function subscribeToNotificationsAsync(connection: HubConnection, userId: string) {
  // The action will happen faster than Hub connection on reload
  // this delay just prevents a user from missing the subscription in that scenario
  await new Promise((resolve) => setTimeout(resolve, 2000));

  while (connection.state !== HubConnectionState.Connected) {
    console.error(`Cannot subscribe to notifications. Retrying...`);
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }

  connection.invoke("SubscribeToNotifications", userId);
  console.debug(`Subscribed to Notifications for User Principal Id: ${userId}`);
}

const dispatchNotificationEvent = (dispatch: any, event: any) => {
  const notificationEvent = event as NotificationEvent;

  dispatch(receiveNotification(notificationEvent));
};

const dispatchNotificationReadEvent = (dispatch: any, event: any) => {
  const notificationReadEvent = event as NotificationReadEvent;

  dispatch(receiveReadNotification(notificationReadEvent));
};

// SignalR Redux Middleware
export const createSignalRMiddleware = (appSettings: AppSettings): Middleware<Dispatch> => {
  // Don't do anything if silent renew
  if (window.location.pathname.endsWith("silent-renew")) {
    console.debug("[SignalR] Middleware disabled during silent renewal.");
    return ({}: MiddlewareAPI) =>
      (next) =>
      async (action: AnyAction) => {
        return next(action);
      };
  }

  // Resolve SignalR Hub URI
  let basePath = `/${appSettings.basePath}`;
  if (basePath !== "/") {
    basePath += "/";
  }
  const connectionHubUri = `${basePath}api/signalr/hub`;

  return ({dispatch}: MiddlewareAPI) =>
    (next) =>
    async (action: AnyAction) => {
      if (action.type === USER_FOUND) {
        const userFoundAction = action as UserFoundAction;

        console.debug(`[SignalR] Connection State: ${signalRState.connection.state}`);

        // Don't recycle the connection if the access token hasn't changed
        if (signalRState.accessTokenExpires == userFoundAction.payload.expires_at) {
          console.debug("[SignalR] Access token unchanged. No changes required.");
          return next(action);
        }

        // Stop the existing connection if there is one
        if (signalRState.accessTokenExpires !== 0) {
          console.debug("[SignalR] Stopping connection...");
          await signalRState.connection.stop();
        }

        console.debug("[SignalR] Configuring connection...");

        // SignalR Configuration
        const transport = HttpTransportType.WebSockets | HttpTransportType.LongPolling;
        const protocol = new JsonHubProtocol();
        const options: IHttpConnectionOptions = {
          transport,
          logMessageContent: false,
          logger: LogLevel.Warning,
          accessTokenFactory: () => userFoundAction.payload.access_token,
        };

        // Create the connection instance
        signalRState.accessTokenExpires = userFoundAction.payload.expires_at;
        signalRState.connection = new HubConnectionBuilder()
          .withUrl(connectionHubUri, options)
          .withHubProtocol(protocol)
          .build();

        // Event Handlers
        const notificationHandler = {} as SignalREventHandler;
        notificationHandler.eventName = "ReceiveNotificationEvent";
        notificationHandler.eventHandler = dispatchNotificationEvent;
        appSettings.signalR.eventHandlers = appSettings.signalR.eventHandlers.concat(notificationHandler);

        const notificationReadHandler = {} as SignalREventHandler;
        notificationReadHandler.eventName = "MarkNotificationAsReadEvent";
        notificationReadHandler.eventHandler = dispatchNotificationReadEvent;
        appSettings.signalR.eventHandlers = appSettings.signalR.eventHandlers.concat(notificationReadHandler);

        appSettings.signalR.eventHandlers.forEach((eventHandler: SignalREventHandler) => {
          signalRState.connection.on(eventHandler.eventName, function (event: any) {
            eventHandler.eventHandler(dispatch, event);
          });
        });

        // Handler to re-establish connection if it's closed
        signalRState.connection.onclose(() => RestartSignalRConnection(dispatch));

        // Start SignalR
        StartSignalRConnection(dispatch);

        // Subscribe to notifications
        subscribeToNotificationsAsync(signalRState.connection, userFoundAction.payload.profile.sub).catch((e) =>
          console.error("Error occurred while subscribing to notifications: " + e)
        );

        return next(action);
      }

      // Action Handlers
      if (appSettings.signalR?.actionHandlers !== undefined) {
        const actionHandler = appSettings.signalR.actionHandlers.find((x) => x.actionType === action.type);
        if (actionHandler !== undefined) {
          console.debug(`[SignalR] Executing Action Handler for Redux Action: ${action.type}`);
          actionHandler.actionHandler(signalRState.connection, dispatch, action);
        }
      }

      return next(action);
    };
};
